import React from 'react';
import { CheckCircle, Clock, DollarSign, Heart, Users, Zap } from 'lucide-react';

const Benefits = ({ language }) => {
  const texts = {
    en: {
      title: 'Why Choose CookingAI365?',
      subtitle: 'Experience the future of smart cooking with our AI-powered assistant',
      benefits: [
        {
          icon: Clock,
          title: 'Save Time',
          description: 'No more meal planning - just snap a photo and get instant recipe suggestions.'
        },
        {
          icon: DollarSign,
          title: 'Reduce Waste',
          description: 'Use ingredients you already have, minimizing food waste and saving money.'
        },
        {
          icon: Heart,
          title: 'Healthier Choices',
          description: 'Get balanced, nutritious recipes based on your available ingredients.'
        },
        {
          icon: Users,
          title: 'Diet Friendly',
          description: 'Personalized recipes for vegetarian, vegan, keto, or any dietary preference.'
        },
        {
          icon: Zap,
          title: 'Always Available',
          description: 'Your AI cooking assistant is ready whenever you need inspiration.'
        },
        {
          icon: CheckCircle,
          title: 'Super Easy',
          description: 'Just point, shoot, and cook - no complex setup required.'
        }
      ]
    },
    pt: {
      title: 'Por Que Escolher CookingAI365?',
      subtitle: 'Experimente o futuro da culinária inteligente com nosso assistente alimentado por IA',
      benefits: [
        {
          icon: Clock,
          title: 'Economize Tempo',
          description: 'Chega de planejar refeições - apenas tire uma foto e receba sugestões de receitas instantâneas'
        },
        {
          icon: DollarSign,
          title: 'Reduza o Desperdício',
          description: 'Use os ingredientes que você já tem, minimizando o desperdício de comida e economizando dinheiro'
        },
        {
          icon: Heart,
          title: 'Escolhas Mais Saudáveis',
          description: 'Obtenha receitas equilibradas e nutritivas com base nos seus ingredientes disponíveis'
        },
        {
          icon: Users,
          title: 'Adequado para Dietas',
          description: 'Receitas personalizadas para vegetarianos, veganos, keto ou qualquer preferência alimentar'
        },
        {
          icon: Zap,
          title: 'Sempre Disponível',
          description: 'Seu assistente de culinária por IA está pronto sempre que precisar de inspiração'
        },
        {
          icon: CheckCircle,
          title: 'Super Fácil',
          description: 'Basta apontar, fotografar e cozinhar - sem configuração complexa necessária'
        }
      ]
    }
  };

  const t = texts[language];

  return (
    <section id="section-4" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.benefits.map((benefit, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <benefit.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {benefit.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* App Preview */}
        <div className="mt-16 text-center">
          <div className="relative inline-block">
            <img
              src="/images/app-recipe-display.jpg"
              alt={language === 'en' ? 'App displaying AI-generated recipe results' : 'Aplicativo exibindo resultados de receitas geradas por IA'}
              className="w-full max-w-lg rounded-2xl shadow-2xl"
            />
            <div className="absolute -top-4 -left-4 bg-green-600 text-white px-6 py-3 rounded-full text-lg font-semibold shadow-lg">
              {language === 'en' ? 'AI-Generated Recipes' : 'Receitas Geradas por IA'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
